import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, EnvelopeSimple, FileText, PaperPlaneTilt, Sparkle } from '@phosphor-icons/react';
import type { SiteContent } from '../../content/site';
import { gsap, EASE } from '../../lib/gsap';
import { prefersReducedMotion } from '../../lib/useReducedMotion';
import { initials, segmentBody } from './typeset';

type Demo = SiteContent['manifesto']['demo'];

/** A raised sheet, a touch lighter than Mineral White (never pure white). */
const SHEET =
	'border border-graphite/[0.07] bg-[#FBFBF7] shadow-[0_18px_40px_-28px_rgba(41,77,59,0.5)] dark:border-mineral/10 dark:bg-forest-deep dark:shadow-none';

/**
 * "Une tâche": an AI agent handling one request, with a human in control.
 *
 * Real markup, not a picture: the request, the agent's steps, the facts it extracted
 * (marked in the request where they were found), the prepared draft, then a real
 * approval button. The default markup is the final state (steps done, approval
 * pending); the section's timelines hide and replay the run (agentRun in motion.ts).
 *
 * Hooks for those timelines: data-agent-card (the box the scene zooms out of),
 * -step, -check, -link, -unread, -mark, -fact, -draft, -await, -approval.
 *
 * Approval: the button stays mounted (focus is kept), turns into its completed
 * style with aria-disabled, and the polite live region next to it announces
 * demo.approved. The decision persists while scrolling back and forth.
 */
export function AgentDemo({ demo, className = '' }: { demo: Demo; className?: string }) {
	const [approved, setApproved] = useState(false);
	const pulse = useRef<HTMLSpanElement>(null);
	const done = useRef<HTMLSpanElement>(null);
	const status = useRef<HTMLParagraphElement>(null);

	const segments = useMemo(
		() => segmentBody(demo.body, demo.extracted.map((fact) => fact.value)),
		[demo.body, demo.extracted],
	);
	const last = demo.steps.length - 1;
	const factsAt = Math.min(1, last);
	const draftAt = Math.max(0, last - 1);

	// Confirmation: the solar ring opens once around the step, the check settles, the sentence slides in.
	useEffect(() => {
		if (!approved || prefersReducedMotion()) return;
		const targets = [pulse.current, done.current, status.current];
		if (targets.some((el) => !el)) return;
		const tl = gsap
			.timeline({ defaults: { ease: EASE } })
			.fromTo(pulse.current, { scale: 1, opacity: 0.9 }, { scale: 2.6, opacity: 0, duration: 1.2 }, 0)
			.fromTo(done.current, { scale: 0.4 }, { scale: 1, duration: 0.7, ease: 'back.out(2.2)' }, 0)
			.fromTo(status.current, { opacity: 0, x: -10 }, { opacity: 1, x: 0, duration: 0.9 }, 0.1);
		return () => {
			tl.kill();
		};
	}, [approved]);

	const message = (
		<div className={`rounded-[16px] p-4 sm:p-5 ${SHEET}`}>
			<div className="flex items-start gap-3">
				<span
					aria-hidden
					className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-sage font-display text-[13px] font-bold tracking-[-0.01em] text-graphite"
				>
					{initials(demo.from)}
				</span>
				<div className="min-w-0 flex-1">
					<p className="flex items-center gap-2 text-[15px] font-bold leading-5 text-graphite dark:text-mineral">
						<span className="truncate">{demo.from}</span>
						{/* Unread: a real state, cleared when the agent has read the request. */}
						<span data-agent-unread aria-hidden className="h-2 w-2 shrink-0 rounded-full bg-solar opacity-0" />
					</p>
					<p className="mt-0.5 truncate text-[14px] leading-5 text-graphite-muted dark:text-dusk-muted">{demo.subject}</p>
				</div>
				<EnvelopeSimple aria-hidden size={18} className="mt-0.5 shrink-0 text-graphite-muted dark:text-dusk-muted" />
			</div>
			<p className="relative isolate mt-3.5 text-[15px] leading-[1.6] text-graphite dark:text-mineral">
				{segments.map((segment, index) =>
					segment.mark === undefined ? (
						<span key={index}>{segment.text}</span>
					) : (
						<mark key={index} className="relative whitespace-nowrap bg-transparent text-inherit">
							<span
								data-agent-mark
								aria-hidden
								className="absolute -inset-x-[3px] inset-y-px -z-10 origin-left rounded-[5px] bg-solar/45 dark:bg-solar/30"
							/>
							{segment.text}
						</mark>
					),
				)}
			</p>
		</div>
	);

	const facts = (
		<dl className="mt-3 grid gap-y-2 rounded-[12px] bg-mist/60 px-3.5 py-3 dark:bg-forest-deep">
			{demo.extracted.map((fact) => (
				<div
					key={fact.label}
					data-agent-fact
					className="grid grid-cols-[5.25rem_minmax(0,1fr)] items-baseline gap-x-3 text-[14px] leading-snug"
				>
					<dt className="text-graphite-muted dark:text-dusk-muted">{fact.label}</dt>
					<dd className="font-medium text-graphite dark:text-mineral">{fact.value}</dd>
				</div>
			))}
		</dl>
	);

	const draft = (
		<div data-agent-draft className={`mt-3 flex w-fit max-w-full items-center gap-2.5 rounded-[12px] py-2 pl-2 pr-3.5 ${SHEET}`}>
			<span aria-hidden className="grid h-8 w-8 shrink-0 place-items-center rounded-[8px] bg-mist text-forest dark:bg-forest-night dark:text-sage">
				<FileText size={18} />
			</span>
			<span className="truncate text-[14px] font-medium text-graphite dark:text-mineral">{demo.from}</span>
		</div>
	);

	const approval = (
		<div data-agent-approval className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
			<button
				type="button"
				onClick={() => setApproved(true)}
				aria-disabled={approved || undefined}
				className={`inline-flex h-11 select-none items-center gap-2 whitespace-nowrap rounded-full px-5 text-[15px] font-medium tracking-[-0.01em] transition-[background-color,color,box-shadow,transform] duration-300 ease-expo ${
					approved
						? 'cursor-default bg-sage/70 text-graphite'
						: 'bg-forest text-mineral shadow-lift hover:bg-forest-hover active:scale-[0.98] dark:bg-solar dark:text-graphite dark:shadow-none dark:hover:bg-solar-hover'
				}`}
			>
				{approved ? (
					<Check aria-hidden size={16} weight="bold" />
				) : (
					<PaperPlaneTilt aria-hidden size={17} weight="regular" />
				)}
				<span>{demo.approve}</span>
			</button>
			<p ref={status} role="status" className="text-[14px] font-medium leading-snug text-forest dark:text-sage">
				{approved ? demo.approved : ''}
			</p>
		</div>
	);

	return (
		<div data-agent-card className={`w-full ${className}`}>
			<p className="flex items-center gap-2 text-[13px] font-medium leading-none text-graphite-muted dark:text-dusk-muted">
				<Sparkle aria-hidden size={15} weight="fill" className="text-forest dark:text-sage" />
				{demo.label}
			</p>

			<div className="mt-4 grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.08fr)] xl:items-start xl:gap-7">
				{message}

				<ol className="grid">
					{demo.steps.map((label, index) => {
						const human = index === last;
						return (
							<li key={label} data-agent-step className="relative grid grid-cols-[1.5rem_minmax(0,1fr)] gap-x-3.5">
								{!human && (
									<span aria-hidden className="absolute bottom-0 left-3 top-7 w-px -translate-x-1/2 bg-graphite/[0.12] dark:bg-mineral/[0.12]">
										<span data-agent-link className="absolute inset-0 origin-top bg-forest/70 dark:bg-sage/70" />
									</span>
								)}

								<span
									aria-hidden
									className="relative mt-px grid h-6 w-6 place-items-center rounded-full border border-graphite/20 bg-mineral dark:border-mineral/20 dark:bg-forest-night"
								>
									{human ? (
										<span data-agent-await className="absolute -inset-px">
											<span
												className={`absolute inset-0 grid place-items-center rounded-full border-2 border-solar bg-mineral transition-opacity dark:bg-forest-night duration-300 ${
													approved ? 'opacity-0' : ''
												}`}
											>
												<span className="h-2 w-2 rounded-full bg-solar" />
											</span>
											<span
												ref={done}
												className={`absolute inset-0 grid place-items-center rounded-full bg-forest text-mineral transition-opacity duration-300 ${
													approved ? '' : 'opacity-0'
												}`}
											>
												<Check size={12} weight="bold" />
											</span>
											<span ref={pulse} className="pointer-events-none absolute inset-0 rounded-full border-2 border-solar opacity-0" />
										</span>
									) : (
										<span
											data-agent-check
											className="absolute -inset-px grid place-items-center rounded-full bg-forest text-mineral"
										>
											<Check size={12} weight="bold" />
										</span>
									)}
								</span>

								<div className={`min-w-0 ${human ? '' : 'pb-5'}`}>
									<p className="text-[15px] font-medium leading-[1.6rem] text-graphite text-pretty dark:text-mineral">{label}</p>
									{index === factsAt && !human && facts}
									{index === draftAt && !human && index !== factsAt && draft}
									{human && approval}
								</div>
							</li>
						);
					})}
				</ol>
			</div>
		</div>
	);
}
